/**
 * Cable Tray Tool - BIM 2025 Grade
 * 
 * Cable tray routing with:
 * - Multiple tray types (Ladder, Solid, Perforated, Wire Mesh)
 * - Fill calculation per NEC
 * - Cable management
 * - Auto-fittings (elbows, tees, crosses)
 * - Support spacing
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Cable Tray Types ====================

export type CableTrayType =
    | 'Ladder'          // Open rungs
    | 'Solid Bottom'    // Solid with sides
    | 'Ventilated'      // Perforated bottom
    | 'Wire Mesh'       // Open wire grid
    | 'Channel';        // U-channel

export type CableTrayMaterial =
    | 'Galvanized Steel'
    | 'Stainless Steel'
    | 'Aluminum'
    | 'Fiberglass';

// ==================== Cable Data ====================

export interface CableEntry {
    id: string;
    name: string;
    type: string;           // e.g., "3C+E 25mm²"
    outerDiameter: number;  // mm
    quantity: number;
    circuitId?: string;
}

export interface CableBundle {
    cables: CableEntry[];
    totalArea: number;      // mm²
}

// ==================== Fill Calculation ====================

export interface FillCalculation {
    trayArea: number;           // mm² - Cross-sectional area
    usedArea: number;           // mm² - Area used by cables
    fillPercentage: number;     // %
    maxFillAllowed: number;     // % - Per NEC/code
    isCompliant: boolean;
    remainingCapacity: number;  // mm²
}

// ==================== Fitting Types ====================

export type CableTrayFittingType =
    | 'Elbow Horizontal 90'
    | 'Elbow Horizontal 45'
    | 'Elbow Vertical Inside 90'
    | 'Elbow Vertical Outside 90'
    | 'Tee Horizontal'
    | 'Cross'
    | 'Reducer'
    | 'End Cap';

export interface CableTrayFitting {
    id: string;
    type: CableTrayFittingType;
    position: [number, number, number];
    rotation: number;
    connectedTrays: string[];
}

// ==================== Enhanced Geometry ====================

export interface EnhancedCableTrayGeometryData {
    // Route path
    start: [number, number, number];
    end: [number, number, number];

    // Cross-section
    trayType: CableTrayType;
    width: number;              // mm
    height: number;             // mm (side rail height)
    thickness: number;          // mm (material thickness)

    // Material
    material: CableTrayMaterial;

    // Level reference
    referenceLevelId: string;
    offset: number;             // mm

    // Fittings
    fittings: CableTrayFitting[];

    // Cables contained
    cables: CableBundle;

    // Support
    supportSpacing: number;     // mm

    // Calculated
    length: number;             // m
    weight: number;             // kg
}

// ==================== Enhanced Cable Tray Element ====================

export interface EnhancedCableTrayElement extends Element {
    category: 'Cable Trays';
    geometryData: EnhancedCableTrayGeometryData;

    // Fill calculation
    fillCalculation: FillCalculation;

    // Connectors
    connectors: {
        start: { id: string; position: [number, number, number]; connectedTo?: string };
        end: { id: string; position: [number, number, number]; connectedTo?: string };
    };
}

// ==================== Tool Options ====================

export interface CableTrayToolOptions {
    // Type
    trayType: CableTrayType;
    familyName: string;
    typeName: string;

    // Size
    width: number;              // mm
    height: number;             // mm

    // Material
    material: CableTrayMaterial;

    // Level
    referenceLevelId: string;
    offset: number;

    // Routing
    chainMode: boolean;
    autoFittings: boolean;

    // Support
    supportSpacing: number;

    // Fill limits
    maxFillPercentage: number;  // Default 50% per NEC
}

// Standard tray sizes (mm)
export const STANDARD_TRAY_WIDTHS = [50, 75, 100, 150, 200, 300, 450, 600, 750, 900];
export const STANDARD_TRAY_HEIGHTS = [50, 75, 100, 150];

// Fill percentages by type (NEC 392.22)
export const MAX_FILL_PERCENTAGES: Record<string, number> = {
    'Ladder-PowerCables': 50,           // Power cables only
    'Ladder-Control': 50,               // Control cables
    'Ladder-Mixed': 50,                 // Mixed
    'SolidBottom-Single': 40,           // Single layer
    'SolidBottom-Multi': 40,            // Multiple layers
    'VentilatedChannel': 50,
    'WireMesh': 50
};

// ==================== Cable Tray Tool Class ====================

export class CableTrayTool extends BaseTool {
    id = 'cable_tray';
    name = 'Cable Tray';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private lastCreatedTray: EnhancedCableTrayElement | null = null;

    private options: CableTrayToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): CableTrayToolOptions {
        return {
            trayType: 'Ladder',
            familyName: 'Cable Tray - Ladder',
            typeName: '300 x 100mm',
            width: 300,
            height: 100,
            material: 'Galvanized Steel',
            referenceLevelId: 'Level 1',
            offset: 3000,           // 3m above level
            chainMode: true,
            autoFittings: true,
            supportSpacing: 1500,   // 1.5m
            maxFillPercentage: 50
        };
    }

    /**
     * Set cable tray options from UI
     */
    setOptions(options: Partial<CableTrayToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): CableTrayToolOptions {
        return { ...this.options };
    }

    /**
     * Calculate fill for given cables
     */
    calculateFill(cables: CableEntry[]): FillCalculation {
        const trayArea = this.options.width * this.options.height;

        let usedArea = 0;
        for (const cable of cables) {
            const cableArea = Math.PI * Math.pow(cable.outerDiameter / 2, 2);
            usedArea += cableArea * cable.quantity;
        }

        const fillPercentage = (usedArea / trayArea) * 100;
        const maxFill = this.options.maxFillPercentage;

        return {
            trayArea,
            usedArea,
            fillPercentage: Math.round(fillPercentage * 10) / 10,
            maxFillAllowed: maxFill,
            isCompliant: fillPercentage <= maxFill,
            remainingCapacity: Math.max(0, (maxFill / 100) * trayArea - usedArea)
        };
    }

    /**
     * Recommend tray size based on cables
     */
    recommendTraySize(cables: CableEntry[]): { width: number; height: number } {
        let totalArea = 0;
        for (const cable of cables) {
            const cableArea = Math.PI * Math.pow(cable.outerDiameter / 2, 2);
            totalArea += cableArea * cable.quantity;
        }

        // Need area / maxFill% = trayArea
        const requiredArea = totalArea / (this.options.maxFillPercentage / 100);

        // Find smallest standard tray that fits
        for (const width of STANDARD_TRAY_WIDTHS) {
            for (const height of STANDARD_TRAY_HEIGHTS) {
                if (width * height >= requiredArea) {
                    return { width, height };
                }
            }
        }

        // Return largest if nothing fits
        return {
            width: STANDARD_TRAY_WIDTHS[STANDARD_TRAY_WIDTHS.length - 1],
            height: STANDARD_TRAY_HEIGHTS[STANDARD_TRAY_HEIGHTS.length - 1]
        };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            if (snapped.distanceTo(this.startPoint) < 0.1) return;

            const tray = this.createCableTray(this.startPoint, snapped);
            this.context.bimStore.addElement(tray);

            // Auto-fitting
            if (this.lastCreatedTray && this.options.autoFittings) {
                this.createFitting(this.lastCreatedTray, tray, this.startPoint);
            }

            this.lastCreatedTray = tray;

            if (this.options.chainMode) {
                this.startPoint = snapped;
            } else {
                this.startPoint = null;
                this.currentPoint = null;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.lastCreatedTray = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview(): void {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            this.context.appStore.setToolPreview({
                type: 'cable_tray',
                start: this.startPoint,
                end: this.currentPoint,
                trayType: this.options.trayType,
                width: this.options.width / 1000,
                height: this.options.height / 1000
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createCableTray(start: THREE.Vector3, end: THREE.Vector3): EnhancedCableTrayElement {
        const length = start.distanceTo(end);
        const weight = this.calculateWeight(length);

        // Empty cable bundle
        const cables: CableBundle = { cables: [], totalArea: 0 };
        const fillCalc = this.calculateFill([]);

        const geometryData: EnhancedCableTrayGeometryData = {
            start: start.toArray() as [number, number, number],
            end: end.toArray() as [number, number, number],
            trayType: this.options.trayType,
            width: this.options.width,
            height: this.options.height,
            thickness: 2,           // 2mm typical
            material: this.options.material,
            referenceLevelId: this.options.referenceLevelId,
            offset: this.options.offset,
            fittings: [],
            cables,
            supportSpacing: this.options.supportSpacing,
            length,
            weight
        };

        const tray: EnhancedCableTrayElement = {
            id: uuidv4(),
            category: 'Cable Trays',
            type: `${this.options.trayType} Tray: ${this.options.width}x${this.options.height}mm`,
            parameters: this.buildParameters(geometryData, fillCalc),
            constraints: this.getDefaultConstraints(),
            geometryData,
            fillCalculation: fillCalc,
            connectors: {
                start: { id: uuidv4(), position: start.toArray() as [number, number, number] },
                end: { id: uuidv4(), position: end.toArray() as [number, number, number] }
            }
        };

        return tray;
    }

    private calculateWeight(length: number): number {
        // Weight per meter based on material and size
        const densities: Record<CableTrayMaterial, number> = {
            'Galvanized Steel': 7850,
            'Stainless Steel': 8000,
            'Aluminum': 2700,
            'Fiberglass': 1800
        };

        const density = densities[this.options.material];
        const thickness = 0.002; // 2mm

        // Approximate cross-section area (two sides + bottom for trough)
        const sideArea = 2 * (this.options.height / 1000) * thickness;
        const bottomArea = (this.options.width / 1000) * thickness;

        // For ladder, bottom is rungs only (~30% of area)
        const bottomFactor = this.options.trayType === 'Ladder' ? 0.3 : 1.0;

        return (sideArea + bottomArea * bottomFactor) * length * density;
    }

    private createFitting(tray1: EnhancedCableTrayElement, tray2: EnhancedCableTrayElement, point: THREE.Vector3): void {
        // Determine angle between trays
        const dir1 = new THREE.Vector3(
            tray1.geometryData.end[0] - tray1.geometryData.start[0],
            tray1.geometryData.end[1] - tray1.geometryData.start[1],
            tray1.geometryData.end[2] - tray1.geometryData.start[2]
        ).normalize();

        const dir2 = new THREE.Vector3(
            tray2.geometryData.end[0] - tray2.geometryData.start[0],
            tray2.geometryData.end[1] - tray2.geometryData.start[1],
            tray2.geometryData.end[2] - tray2.geometryData.start[2]
        ).normalize();

        const angle = Math.acos(Math.abs(dir1.dot(dir2))) * (180 / Math.PI);

        if (angle > 10) {
            // Create fitting record
            const fitting: CableTrayFitting = {
                id: uuidv4(),
                type: Math.abs(angle - 90) < 10 ? 'Elbow Horizontal 90' : 'Elbow Horizontal 45',
                position: point.toArray() as [number, number, number],
                rotation: 0,
                connectedTrays: [tray1.id, tray2.id]
            };

            tray1.geometryData.fittings.push(fitting);
        }
    }

    private buildParameters(geom: EnhancedCableTrayGeometryData, fill: FillCalculation): Record<string, any> {
        return {
            // Dimensions
            'Width': geom.width,
            'Height': geom.height,
            'Length': Math.round(geom.length * 1000) / 1000,
            'Tray Type': geom.trayType,

            // Material
            'Material': geom.material,

            // Level
            'Reference Level': geom.referenceLevelId,
            'Offset': geom.offset,

            // Fill
            'Fill Percentage': fill.fillPercentage,
            'Max Fill Allowed': fill.maxFillAllowed,
            'Fill Compliant': fill.isCompliant,
            'Remaining Capacity': Math.round(fill.remainingCapacity),

            // Support
            'Support Spacing': geom.supportSpacing,
            'Support Count': Math.ceil(geom.length * 1000 / geom.supportSpacing) + 1,

            // Physical
            'Weight': Math.round(geom.weight * 10) / 10
        };
    }

    private getDefaultConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.referenceLevelId,
                data: { offset: this.options.offset }
            }
        ];
    }
}

// ==================== Helper Functions ====================

/**
 * Add cable to tray and recalculate fill
 */
export function addCableToTray(tray: EnhancedCableTrayElement, cable: CableEntry): boolean {
    const newCables = [...tray.geometryData.cables.cables, cable];

    // Check fill
    let usedArea = 0;
    for (const c of newCables) {
        usedArea += Math.PI * Math.pow(c.outerDiameter / 2, 2) * c.quantity;
    }

    const fillPercentage = (usedArea / tray.fillCalculation.trayArea) * 100;

    if (fillPercentage > tray.fillCalculation.maxFillAllowed) {
        return false; // Would exceed fill limit
    }

    // Update
    tray.geometryData.cables.cables.push(cable);
    tray.geometryData.cables.totalArea = usedArea;
    tray.fillCalculation.usedArea = usedArea;
    tray.fillCalculation.fillPercentage = Math.round(fillPercentage * 10) / 10;
    tray.fillCalculation.remainingCapacity =
        (tray.fillCalculation.maxFillAllowed / 100) * tray.fillCalculation.trayArea - usedArea;
    tray.fillCalculation.isCompliant = true;

    return true;
}

